import { useTranslation } from 'react-i18next';
import { BaggageCategory } from '@/api/generated/schemas';
import { SegmentedField, TextField } from '@/shared/forms';
import { Button, Dialog, toast } from '@/shared/ui';
import { serverErrorDetail } from '@/shared/lib/problemDetails';
import {
  BAGGAGE_PRICING_MODES,
  BAGGAGE_PURCHASE_CHANNELS,
  type BaggagePricingMode,
  type BaggagePurchaseChannel,
  type BaggageRuleFormValues,
} from '../baggageRuleFormSchema';
import { useBaggageRuleForm } from '../useBaggageRuleForm';
import type { CarrierCurrency } from './AirlineBaggagePanel';

interface BaggageRuleDialogProps {
  airlineId: number;
  initialValues: BaggageRuleFormValues;
  /** Uložená měna dopravce; `null` = dopravce měnu nemá, takže cenu nelze zadat. */
  carrierCurrency: CarrierCurrency | null;
  onClose: () => void;
}

/**
 * Dialog jednoho pravidla zavazadla. Ukládá se **okamžitě vlastním endpointem**, nezávisle na
 * formuláři letecké společnosti — uživatel tak nepřijde o rozpracovaná pravidla, když odejde bez
 * uložení hlavního formuláře.
 */
export function BaggageRuleDialog({
  airlineId,
  initialValues,
  carrierCurrency,
  onClose,
}: BaggageRuleDialogProps) {
  const { t } = useTranslation('inventory');
  const isEdit = initialValues.id !== null;

  const { form, submit, isSubmitting } = useBaggageRuleForm({
    airlineId,
    initialValues,
    hasCarrierCurrency: carrierCurrency !== null,
    onSaved: () => {
      onClose();
      toast({ severity: 'success', description: t('baggage.toast.saved') });
    },
    onError: (error) =>
      // Business pravidla (chybějící měna dopravce) nesou konkrétní větu v `detail` (RFC 9457) —
      // bez ní by uživatel viděl jen „nepodařilo se uložit" a nevěděl, co má opravit.
      toast({
        severity: 'error',
        title: t('baggage.toast.errorTitle'),
        description: serverErrorDetail(error) ?? t('baggage.toast.saveError'),
      }),
  });

  const categoryOptions = Object.values(BaggageCategory).map((value) => ({
    value,
    label: t(`baggageCategory.${value}`),
  }));

  const pricingOptions = BAGGAGE_PRICING_MODES.map((value) => ({
    value,
    label: t(`baggagePricing.${value}`),
  }));

  const channelOptions = BAGGAGE_PURCHASE_CHANNELS.map((value) => ({
    value,
    label: t(`baggagePurchaseChannel.${value}`),
  }));

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? t('baggage.dialog.editTitle') : t('baggage.dialog.addTitle')}
      icon="tag"
      width="lg"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('baggage.cancel')}
          </Button>
          {/* Zašedlé Uložit je tady v pořádku (na rozdíl od dvouzáložkového detailu): dialog má
              jedinou sekci, takže chybějící údaj je vždycky vidět na obrazovce. */}
          <form.Subscribe selector={(state) => state.canSubmit}>
            {(canSubmit) => (
              <Button
                type="button"
                variant="primary"
                loading={isSubmitting}
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                {t('baggage.save')}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <div className="gap-phx-4 flex flex-col">
        <form.Field name="category">
          {(field) => (
            <SegmentedField<BaggageCategory>
              field={field}
              layout="stacked"
              label={t('baggage.category.label')}
              required
              helperText={t('baggage.category.hint')}
              options={categoryOptions}
              testId="baggage-category"
            />
          )}
        </form.Field>

        <div className="gap-phx-4 grid grid-cols-1 md:grid-cols-2">
          <form.Field name="name">
            {(field) => (
              <TextField
                field={field}
                label={t('baggage.name.label')}
                placeholder={t('baggage.name.placeholder')}
                required
                maxLength={100}
                data-testid="baggage-name"
              />
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <TextField
                field={field}
                label={t('baggage.description.label')}
                placeholder={t('baggage.description.placeholder')}
                maxLength={255}
              />
            )}
          </form.Field>
        </div>

        <div className="gap-phx-4 grid grid-cols-2 md:grid-cols-4">
          <form.Field name="widthCm">
            {(field) => (
              <TextField
                field={field}
                type="number"
                min={1}
                label={t('baggage.widthCm.label')}
                helperText={t('baggage.dimensions.hint')}
              />
            )}
          </form.Field>

          <form.Field name="heightCm">
            {(field) => (
              <TextField field={field} type="number" min={1} label={t('baggage.heightCm.label')} />
            )}
          </form.Field>

          <form.Field name="depthCm">
            {(field) => (
              <TextField field={field} type="number" min={1} label={t('baggage.depthCm.label')} />
            )}
          </form.Field>

          <form.Field name="weightKg">
            {(field) => (
              <TextField field={field} type="number" min={1} label={t('baggage.weightKg.label')} />
            )}
          </form.Field>
        </div>

        <div className="gap-phx-4 grid grid-cols-1 md:grid-cols-2">
          <form.Field
            name="pricing"
            listeners={{
              // Pravidlo v ceně letenky cenu nemá — pole se mažou hned při přepnutí, aby se
              // uživateli nevrátila neviditelná čísla při návratu na „za příplatek".
              onChange: ({ value }) => {
                if (value === 'Included') {
                  form.setFieldValue('priceFrom', '');
                  form.setFieldValue('priceTo', '');
                }
              },
            }}
          >
            {(field) => (
              <SegmentedField<BaggagePricingMode>
                field={field}
                layout="stacked"
                label={t('baggage.pricing.label')}
                required
                options={pricingOptions}
                testId="baggage-pricing"
              />
            )}
          </form.Field>

          <form.Field name="maxPiecesPerPassenger">
            {(field) => (
              <TextField
                field={field}
                type="number"
                min={1}
                label={t('baggage.maxPieces.label')}
                required
                helperText={t('baggage.maxPieces.hint')}
                data-testid="baggage-max-pieces"
              />
            )}
          </form.Field>
        </div>

        <form.Field name="purchaseChannels">
          {(field) => (
            <SegmentedField<BaggagePurchaseChannel>
              field={field}
              multiple
              layout="stacked"
              label={t('baggage.purchaseChannels.label')}
              required
              options={channelOptions}
              testId="baggage-purchase-channels"
            />
          )}
        </form.Field>

        <form.Subscribe selector={(state) => state.values.pricing}>
          {(pricing) =>
            pricing === 'Included' ? null : (
              <div className="gap-phx-4 grid grid-cols-1 md:grid-cols-2">
                <form.Field name="priceFrom">
                  {(field) => (
                    <TextField
                      field={field}
                      type="number"
                      min={0}
                      label={t('baggage.priceFrom.label')}
                      helperText={
                        carrierCurrency === null
                          ? t('baggage.price.noCurrencyHint')
                          : t('baggage.price.hint', { currency: carrierCurrency.code })
                      }
                      data-testid="baggage-price-from"
                    />
                  )}
                </form.Field>

                <form.Field name="priceTo">
                  {(field) => (
                    <TextField
                      field={field}
                      type="number"
                      min={0}
                      label={t('baggage.priceTo.label')}
                    />
                  )}
                </form.Field>
              </div>
            )
          }
        </form.Subscribe>
      </div>
    </Dialog>
  );
}
